/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { NavigateFunction } from 'react-router';

/* Import API functions */
import { EeCheckApiError, requestApiToken } from '@/api/eeCheckApi';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';

/* Import libraries */
import { appendDebugEntry, createResponsePreview } from '@/lib/app/debugRequests';
import {
    clearPersistedSessionState,
    consumeLoginRedirect,
    persistSessionState,
    readStoredSessionState,
    readStoredSessionUsername,
    storeLoginRedirect,
    type StoredSessionState,
} from '@/lib/app/sessionState';

/* Import types */
import type { LocalizedMessage } from '@/types/i18n';
import type { ApiTokenCredentials } from '@/types/eeCheck';
import type { DebugRequestEntry, SessionOrigin } from '@/types/ui';

type Translate = (key: string, values?: Record<string, number | string>) => string;

interface UseAppSessionOptions {
    navigate: NavigateFunction;
    onCatalogChanged: (username: string) => void;
    onResetAppView: () => void;
    setDebugEntries: Dispatch<SetStateAction<DebugRequestEntry[]>>;
    t: Translate;
}

export function useAppSession({
    navigate,
    onCatalogChanged,
    onResetAppView,
    setDebugEntries,
    t,
}: UseAppSessionOptions) {
    // Restore the session before children mount so data views never request once without a token and again with it.
    const [restoredSession] = useState<StoredSessionState | null>(readStoredSessionState);
    const [accessToken, setAccessToken] = useState(restoredSession?.accessToken ?? '');
    const [sessionOrigin, setSessionOrigin] = useState<SessionOrigin | null>(restoredSession?.origin ?? null);
    const [sessionImportedAt, setSessionImportedAt] = useState(restoredSession?.importedAt ?? '');
    const [sessionUsername, setSessionUsername] = useState(() => (restoredSession ? readStoredSessionUsername() : ''));
    const [sessionStatus, setSessionStatus] = useState<LocalizedMessage>(() => (restoredSession ?
        { key: 'Using API token restored from session storage.' } :
        { key: 'No API token available.' }
    ));
    const isSessionStateReady = true;
    const [isSessionExpired, setIsSessionExpired] = useState(false);
    const [isSubmittingLogin, setIsSubmittingLogin] = useState(false);
    const [loginErrorMessage, setLoginErrorMessage] = useState('');
    const credentialsRef = useRef<ApiTokenCredentials | null>(null);

    function clearSessionState(message: LocalizedMessage) {
        clearPersistedSessionState(message, {
            setAccessToken,
            setSessionImportedAt,
            setSessionOrigin,
            setSessionStatus,
            setSessionUsername,
        });
        onCatalogChanged('');
    }

    function handleClearSession() {
        setIsSessionExpired(false);
        onResetAppView();
        credentialsRef.current = null;
        clearSessionState({ key: 'No API token available.' });
        navigate(APP_ROUTES.login, { replace: true });
    }

    function handleSessionExpired() {
        const currentPath = window.location.hash.startsWith('#')
            ? window.location.hash.slice(1)
            : '';

        storeLoginRedirect(currentPath);
        handleClearSession();
    }

    async function handleLoginSubmit(credentials: ApiTokenCredentials) {
        setLoginErrorMessage('');
        setIsSubmittingLogin(true);

        try {
            const { data, meta } = await requestApiToken(credentials);

            appendDebugEntry(
                setDebugEntries,
                meta.url,
                { username: credentials.username },
                { method: meta.method },
                true,
                createResponsePreview(JSON.stringify({ token: '[redacted]' })),
                null,
            );
            credentialsRef.current = credentials;
            persistSessionState(data.token, 'login', {
                setAccessToken,
                setSessionImportedAt,
                setSessionOrigin,
                setSessionUsername,
            }, undefined, credentials.username);
            setIsSessionExpired(false);
            setSessionStatus({ key: 'API token acquired successfully.' });
            onCatalogChanged(credentials.username);

            navigate(consumeLoginRedirect(APP_ROUTES.home), { replace: true });
        } catch (error) {
            const message = error instanceof Error ? t(error.message) : t('Unknown login error.');

            appendDebugEntry(
                setDebugEntries,
                error instanceof EeCheckApiError ? error.url : '/internal/auth/token-login',
                { username: credentials.username },
                { method: 'POST' },
                false,
                createResponsePreview(error instanceof EeCheckApiError ? error.rawResponse : message),
                null,
            );
            clearPersistedSessionState({
                key: 'Login failed: {message}',
                values: { message },
            }, {
                setAccessToken,
                setSessionImportedAt,
                setSessionOrigin,
                setSessionStatus,
                setSessionUsername,
            });
            setLoginErrorMessage(message);
        } finally {
            setIsSubmittingLogin(false);
        }
    }

    return {
        handleClearSession,
        handleLoginSubmit,
        handleSessionExpired,
        clearSessionState,
        isSessionExpired,
        isSessionStateReady,
        isSubmittingLogin,
        loginErrorMessage,
        accessToken,
        sessionImportedAt,
        sessionOrigin,
        sessionStatus,
        sessionUsername,
        setSessionStatus,
    };
}
